import { useEffect, useState } from "react";

export default function SchoolDetails({
  school,
  onBack,
}) {
  const photos = Array.isArray(school?.photos)
    ? school.photos
    : [];

  const videos = Array.isArray(school?.videos)
    ? school.videos
    : [];

  const [activeTab, setActiveTab] = useState(
    photos.length > 0 ? "photos" : "videos"
  );

  const [selectedImage, setSelectedImage] =
    useState(null);

  /* ======================================================
     RESET WHEN SCHOOL CHANGES
  ====================================================== */

  useEffect(() => {
    setActiveTab(
      photos.length > 0
        ? "photos"
        : "videos"
    );

    setSelectedImage(null);
  }, [
    school?.id,
    photos.length,
    videos.length,
  ]);

  /* ======================================================
     ESCAPE TO CLOSE IMAGE
  ====================================================== */

  useEffect(() => {
    if (!selectedImage) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedImage(null);
      }
    };

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [selectedImage]);

  if (!school) {
    return null;
  }

  return (
    <div className="ms-school-detail">
      {/* =================================================
          TOP BAR
      ================================================= */}

      <div className="ms-school-detail-topbar">
        <button
          type="button"
          className="ms-school-detail-back"
          onClick={onBack}
        >
          <span>←</span>

          Back to Schools
        </button>

        <span className="ms-school-detail-network">
          MINDSPARK NETWORK
        </span>
      </div>

      {/* =================================================
          SCHOOL PROFILE
      ================================================= */}

      <section className="ms-school-detail-profile">
        <div className="ms-school-detail-profile-main">
          {/* LOGO */}

          <div className="ms-school-detail-logo">
            {school.logo ? (
              <img
                src={school.logo}
                alt={`${school.schoolName} logo`}
              />
            ) : (
              <span>
                {school.schoolName
                  ?.charAt(0)
                  ?.toUpperCase()}
              </span>
            )}
          </div>

          {/* INFORMATION */}

          <div className="ms-school-detail-info">
            <div className="ms-school-detail-kicker">
              <span />

              MINDSPARK LEARNING PARTNER
            </div>

            <h1>
              {school.schoolName}
            </h1>

            <div className="ms-school-detail-location">
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="M12 21s6-5.3 6-11a6 6 0 1 0-12 0c0 5.7 6 11 6 11Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />

                <circle
                  cx="12"
                  cy="10"
                  r="2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
              </svg>

              <span>
                {school.schoolAddress ||
                  "Location not available"}
              </span>
            </div>

            <p>
              Explore moments from MindSpark's
              hands-on learning experiences,
              activities and technology sessions
              with this learning community.
            </p>
          </div>
        </div>

        {/* MEDIA SUMMARY */}

        <div className="ms-school-detail-summary">
          <div className="ms-school-detail-summary-head">
            <span>
              LEARNING GALLERY
            </span>

            <strong>
              Explore
            </strong>
          </div>

          <div className="ms-school-detail-summary-stats">
            <div className="yellow">
              <strong>
                {String(
                  photos.length
                ).padStart(2, "0")}
              </strong>

              <span>
                Photos
              </span>
            </div>

            <div className="blue">
              <strong>
                {String(
                  videos.length
                ).padStart(2, "0")}
              </strong>

              <span>
                Videos
              </span>
            </div>
          </div>

          <p>
            Learning Through Technology
          </p>
        </div>
      </section>

      {/* =================================================
          MEDIA AREA
      ================================================= */}

      <section className="ms-school-detail-media-section">
        <div className="ms-school-detail-media-header">
          <div>
            <span className="ms-school-detail-section-label">
              SCHOOL EXPERIENCES
            </span>

            <h2>
              Moments from the
              <span> learning journey.</span>
            </h2>
          </div>

          {/* TABS */}

          <div
            className="ms-school-detail-tabs"
            role="tablist"
            aria-label="School media"
          >
            <button
              type="button"
              role="tab"
              aria-selected={
                activeTab === "photos"
              }
              className={
                activeTab === "photos"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setActiveTab("photos")
              }
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <rect
                  x="3"
                  y="5"
                  width="18"
                  height="14"
                  rx="2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />

                <circle
                  cx="9"
                  cy="10"
                  r="2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />

                <path
                  d="m5 17 5-4 3 2 2-2 4 4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
              </svg>

              Photos

              <span>
                {photos.length}
              </span>
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={
                activeTab === "videos"
              }
              className={
                activeTab === "videos"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setActiveTab("videos")
              }
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <rect
                  x="3"
                  y="5"
                  width="14"
                  height="14"
                  rx="2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />

                <path
                  d="m17 10 4-2v8l-4-2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
              </svg>

              Videos

              <span>
                {videos.length}
              </span>
            </button>
          </div>
        </div>

        {/* =================================================
            PHOTOS
        ================================================= */}

        {activeTab === "photos" && (
          <>
            {photos.length > 0 ? (
              <div
                className={`ms-school-photo-gallery ${
                  photos.length === 1
                    ? "single"
                    : ""
                }`}
              >
                {photos.map(
                  (photo, index) => (
                    <button
                      type="button"
                      key={`${photo}-${index}`}
                      className="ms-school-photo-card"
                      onClick={() =>
                        setSelectedImage(
                          photo
                        )
                      }
                    >
                      <img
                        src={photo}
                        alt={`${school.schoolName} activity ${
                          index + 1
                        }`}
                        loading={
                          index < 2
                            ? "eager"
                            : "lazy"
                        }
                      />

                      <div className="ms-school-photo-overlay">
                        <div>
                          <span>
                            PHOTO{" "}
                            {String(
                              index + 1
                            ).padStart(
                              2,
                              "0"
                            )}
                          </span>

                          <strong>
                            View full image
                          </strong>
                        </div>

                        <div className="ms-school-photo-open">
                          ↗
                        </div>
                      </div>
                    </button>
                  )
                )}
              </div>
            ) : (
              <MediaEmptyState
                type="photos"
              />
            )}
          </>
        )}

        {/* =================================================
            VIDEOS
        ================================================= */}

        {activeTab === "videos" && (
          <>
            {videos.length > 0 ? (
              <div
                className={`ms-school-video-gallery ${
                  videos.length === 1
                    ? "single"
                    : ""
                }`}
              >
                {videos.map(
                  (video, index) => (
                    <article
                      key={`${video}-${index}`}
                      className="ms-school-video-card"
                    >
                      <div className="ms-school-video-frame">
                        <video
                          controls
                          controlsList="nodownload"
                          onContextMenu={(event) =>
                            event.preventDefault()
                          }
                          playsInline
                          preload="metadata"
                        >
                          <source
                            src={video}
                            type="video/mp4"
                          />

                          Your browser does
                          not support video.
                        </video>
                      </div>

                      <div className="ms-school-video-info">
                        <span>
                          VIDEO{" "}
                          {String(
                            index + 1
                          ).padStart(
                            2,
                            "0"
                          )}
                        </span>

                        <strong>
                          Learning Experience
                        </strong>
                      </div>
                    </article>
                  )
                )}
              </div>
            ) : (
              <MediaEmptyState
                type="videos"
              />
            )}
          </>
        )}
      </section>

      {/* =================================================
          BOTTOM NAVIGATION
      ================================================= */}

      <div className="ms-school-detail-bottom">
        <div>
          <span>
            MINDSPARK EDUTECH
          </span>

          <strong>
            Learning Through Technology
          </strong>
        </div>

        <button
          type="button"
          onClick={onBack}
        >
          <span>←</span>
          Explore More Schools
        </button>
      </div>

      {/* =================================================
          FULL SCREEN IMAGE
      ================================================= */}

      {selectedImage && (
        <div
          className="ms-school-image-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Full screen school image"
          onClick={() =>
            setSelectedImage(null)
          }
        >
          <button
            type="button"
            className="ms-school-image-close"
            onClick={() =>
              setSelectedImage(null)
            }
            aria-label="Close image"
          >
            ×
          </button>

          <img
            src={selectedImage}
            alt={`${school.schoolName} full screen`}
            onClick={(event) =>
              event.stopPropagation()
            }
          />
        </div>
      )}
    </div>
  );
}


/* ==========================================================
   EMPTY MEDIA STATE
========================================================== */

function MediaEmptyState({ type }) {
  return (
    <div className="ms-school-media-empty">
      <div>
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <rect
            x="4"
            y="5"
            width="16"
            height="14"
            rx="2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
          />

          <path
            d="m7 16 3-3 2 2 2-2 3 3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
          />
        </svg>
      </div>

      <h3>
        No {type} available yet
      </h3>

      <p>
        New learning moments will appear
        here when they are added.
      </p>
    </div>
  );
}
